import React, { ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { GetWine_wine } from "../../../types/__generated__/GetWine";
import { GetWineRelatedWines } from "../../../types/__generated__/GetWineRelatedWines";
import { WINE_RELATED_WINES } from "../../../graphql/resolvers/wine";

import {
  FETCH_POLICY_NETWORK_ONLY,
  I18N_NAMESPACES,
  WINE_RELATED_WINES_LIMIT,
  SIDE_PANELS,
} from "../../../helpers/constants";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { MemberRewardBasicInfo } from "../../../types/__generated__/MemberRewardBasicInfo";
import { extractPropsForWineCard } from "../../../helpers/tools";
import { colors } from "../../../styles/variables";
import Loading from "../../atoms/Loading";
import WineCard from "../WineCard";
import ArrowNavigator from "../../atoms/ArrowNavigator";
import WineDetailsTray from "../../organisms/WineDetailsTray";
import WineListsSidePanel from "../../organisms/WineListsSidePanel";

import {
  ContentContainer,
  Header,
  WineCardContainer,
  WineCardWrapper,
  WineCardsWrapper,
  ArrowWrapper,
} from "./style";

interface WineRelatedWinesProps {
  wine: GetWine_wine | null | undefined;
  memberShoppingCartQuery?: MemberShoppingCart | MemberRewardBasicInfo | null | undefined;
  isInBoxAndCartTray?: boolean;
  memberId?: number;
  handleToggleBottleInfoTray?: Function;
  isWineDetailsPage?: boolean;
  locationId?: number;
}

const WineRelatedWines = ({
  wine,
  memberShoppingCartQuery,
  isInBoxAndCartTray = false,
  memberId,
  handleToggleBottleInfoTray,
  isWineDetailsPage,
  locationId,
}: WineRelatedWinesProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.WINE_PAGES, I18N_NAMESPACES.COMMON]);
  const history = useHistory();
  const [wineSlug, setWineSlug] = useState<string | null>(null);
  const [currentPanel, setCurrentPanel] = useState<string | null>(null);
  const [selectedWine, setSelectedWine] = useState<object | null>(null);

  const horizontalContainer = useRef<HTMLDivElement | null>(null);

  const slug = wine?.product?.slug;

  const { data: wineRelatedWinesData, loading: wineRelatedWinesLoading } = useQuery<GetWineRelatedWines>(
    WINE_RELATED_WINES,
    {
      skip: !slug,
      variables: {
        slug,
        locationId,
        limit: WINE_RELATED_WINES_LIMIT,
      },
      partialRefetch: true,
      fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
    }
  );

  if (
    !wineRelatedWinesLoading &&
    (!wineRelatedWinesData?.wine?.similarWines || // NULL
      wineRelatedWinesData?.wine?.similarWines?.length === 0) // EMPTY
  ) {
    return null;
  }

  const similarWines = wineRelatedWinesData?.wine?.similarWines || [];
  const withMoreSliderShown = (similarWines?.length ?? 0) > 1;

  const renderWineDetailsTray = (): ReactElement => (
    <WineDetailsTray
      handleToggleBottleInfoTray={() => setWineSlug(null)}
      memberShoppingCartQuery={memberShoppingCartQuery}
      slug={wineSlug}
      isInBoxAndCartTray={isInBoxAndCartTray}
      locationId={locationId}
    />
  );

  const renderContent = (): ReactElement => (
    <WineCardContainer>
      {withMoreSliderShown && (
        <ArrowWrapper isWineDetailsPage={!!isWineDetailsPage}>
          <ArrowNavigator horizontalContainer={horizontalContainer} scrollAdjustment={0} />
        </ArrowWrapper>
      )}
      <WineCardsWrapper ref={horizontalContainer} length={similarWines?.length || 0}>
        {wineRelatedWinesData?.wine?.similarWines?.map((similarWine) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const wineData: any = extractPropsForWineCard(similarWine ?? {});
          const similarWineSlug = similarWine?.product?.slug;
          const matchPercentage = similarWine?.memberLikelihood
            ? Math.round(similarWine.memberLikelihood * 100)
            : 0;

          return (
            similarWine?.id && (
              <WineCardWrapper key={similarWine?.id}>
                <WineCard
                  /* @ts-ignore */
                  memberShoppingCartQuery={memberShoppingCartQuery}
                  wine={similarWine}
                  image={wineData?.image}
                  name={wineData?.name}
                  country={wineData?.country}
                  regionState={wineData?.regionState}
                  slug={wineData?.slug}
                  region={wineData?.region}
                  wineClassName={wineData?.wineClassName}
                  wineMakerName={wineData?.wineMakerName}
                  price={wineData?.price}
                  memberLikelihood={matchPercentage.toString()}
                  isInWineCollections
                  toggleWineDetailsTray={() => {
                    if (similarWineSlug) {
                      setWineSlug(similarWineSlug);
                    }
                  }}
                  wineClassId={wineData?.wineClassId}
                  wineTypeId={wineData?.wineTypeId}
                  title={wineData?.title}
                  key={similarWine?.id}
                  toggleBookmarksTray={() => {
                    setCurrentPanel(SIDE_PANELS.SAVE_WINE_TO_WINE_LIST);
                    setSelectedWine(similarWine);
                  }}
                  toggleAddBottleTray={() => {
                    setCurrentPanel(SIDE_PANELS.ADD_BOTTLE);
                    setSelectedWine(similarWine);
                  }}
                  onTagClicked={() => {
                    setWineSlug(null);
                    handleToggleBottleInfoTray && handleToggleBottleInfoTray();
                  }}
                />
              </WineCardWrapper>
            )
          );
        })}
      </WineCardsWrapper>
    </WineCardContainer>
  );

  return (
    <ContentContainer isWineDetailsPage={!!isWineDetailsPage}>
      <Header color={colors.black}>{t("winePages:wineRelatedWine.headerTop")}</Header>
      <Header color={colors.coral}>{t("winePages:wineRelatedWine.headerBottom")}</Header>
      {wineRelatedWinesLoading ? <Loading /> : renderContent()}
      {wineSlug && renderWineDetailsTray()}
      {currentPanel === SIDE_PANELS.SAVE_WINE_TO_WINE_LIST && (
        <WineListsSidePanel
          handleOffCanvasChange={() => {
            setCurrentPanel(null);
            setSelectedWine(null);
          }}
          memberId={memberId}
          wine={selectedWine}
          history={history}
        />
      )}
    </ContentContainer>
  );
};

export default WineRelatedWines;
